import { RefreshCw, X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";

interface Props {
  changes: number;
  onUpdateNote: () => void;
  onDismiss: () => void;
  // The v3.2 workspace: a floating v3.2 bar (.chg-bar).
  v3?: boolean;
}

// Shown above the note after the transcript was corrected: the note is not
// changed until the clinician asks for a proposal and approves it.
export const TranscriptChangedBar = ({
  changes,
  onUpdateNote,
  onDismiss,
  v3 = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  if (v3)
    return (
      <div
        className="sticky top-2 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[18px] bg-v3-panel py-1.5 pl-3 pr-1.5 font-v3 shadow-v3-float sm:flex-nowrap sm:rounded-full"
        data-testid="transcript-changed-bar"
        role="status"
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-v3-brand-tint text-v3-brand">
          <RefreshCw aria-hidden className="size-3.5" />
        </span>
        <p className="min-w-0 flex-1 text-[13.5px] leading-5 text-v3-text-2">
          <b className="font-semibold text-v3-text">
            {t("visit.transcriptEdit.changedTitle")}
          </b>{" "}
          · {t("visit.transcriptEdit.changedBody")}
        </p>
        <V3IconButton
          className="sm:order-last"
          label={t("visit.transcriptEdit.notNow")}
          onClick={onDismiss}
        >
          <X />
        </V3IconButton>
        <V3Button
          className="w-full sm:w-auto"
          data-testid="transcript-update-note"
          variant="primary"
          onClick={onUpdateNote}
        >
          {t("visit.transcriptEdit.updateNote", { count: changes })}
        </V3Button>
      </div>
    );
  return (
    <div
      className="sticky top-2 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border bg-card py-2 pl-3 pr-1.5 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.35)] sm:flex-nowrap sm:rounded-full sm:pl-3.5"
      data-testid="transcript-changed-bar"
      role="status"
    >
      <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <RefreshCw aria-hidden className="size-3.5" />
      </span>
      <p className="min-w-0 flex-1 text-[13.5px] leading-5 text-muted-foreground">
        <b className="font-semibold text-foreground">
          {t("visit.transcriptEdit.changedTitle")}
        </b>{" "}
        · {t("visit.transcriptEdit.changedBody")}
      </p>
      <Button
        aria-label={t("visit.transcriptEdit.notNow")}
        className="size-11 shrink-0 sm:order-last sm:size-9"
        size="icon"
        title={t("visit.transcriptEdit.notNow")}
        type="button"
        variant="ghost"
        onClick={onDismiss}
      >
        <X />
      </Button>
      <Button
        className="h-11 w-full rounded-full sm:h-9 sm:w-auto"
        data-testid="transcript-update-note"
        type="button"
        onClick={onUpdateNote}
      >
        {t("visit.transcriptEdit.updateNote", { count: changes })}
      </Button>
    </div>
  );
};
